import {
  Anchor,
  Box,
  Collapse,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useWarehouseOptions } from "../hooks/useWarehouseOptions"
import { useExternalPartiesQuery } from "../hooks/useExternalPartiesQuery"

export type SourceType = "INTERNAL_WAREHOUSE" | "EXTERNAL_PARTY"

interface Props {
  sourceType: SourceType
  fromWarehouseId: string
  toWarehouseId: string
  externalPartyId: string
  externalPartyContactName: string
  externalPartyContactPhone: string
  externalPartyAddressOverride: string
  errors: {
    fromWarehouseId?: string | null
    toWarehouseId?: string | null
    externalPartyId?: string | null
  }
  onFromWarehouseChange: (value: string) => void
  onToWarehouseChange: (value: string) => void
  onExternalPartyChange: (value: string) => void
  onContactNameChange: (value: string) => void
  onContactPhoneChange: (value: string) => void
  onAddressOverrideChange: (value: string) => void
}

interface ExternalPartyFieldsProps {
  externalPartyId: string
  externalPartyContactName: string
  externalPartyContactPhone: string
  externalPartyAddressOverride: string
  externalPartyIdError?: string | null
  onExternalPartyChange: (value: string) => void
  onContactNameChange: (value: string) => void
  onContactPhoneChange: (value: string) => void
  onAddressOverrideChange: (value: string) => void
}

function ExternalPartyFields({
  externalPartyId,
  externalPartyContactName,
  externalPartyContactPhone,
  externalPartyAddressOverride,
  externalPartyIdError,
  onExternalPartyChange,
  onContactNameChange,
  onContactPhoneChange,
  onAddressOverrideChange,
}: ExternalPartyFieldsProps) {
  const { t } = useTranslation("inbound")
  const { data: partyResp } = useExternalPartiesQuery()
  const partyRows = partyResp?.rows ?? []
  const partyOptions = partyRows.map((p) => ({ value: p.id, label: p.name }))
  const selectedParty = partyRows.find((p) => p.id === externalPartyId) ?? null
  const [overrideOpen, setOverrideOpen] = useState(false)

  const partyPlaceholder =
    partyResp == null
      ? t("sourcePanel.loading")
      : partyOptions.length === 0
        ? t("sourcePanel.noExternalParty")
        : t("sourcePanel.selectParty")

  return (
    <>
      <Select
        label={t("sourcePanel.externalParty")}
        placeholder={partyPlaceholder}
        data={partyOptions}
        value={externalPartyId || null}
        onChange={(v) => onExternalPartyChange(v ?? "")}
        error={externalPartyIdError ?? undefined}
        searchable
        required
      />
      {selectedParty && (
        <Box>
          <Text size="sm" c="dimmed">
            {t("sourcePanel.address")}:{" "}
            <Text component="span" c="dark.6">
              {selectedParty.address ?? "—"}
              {selectedParty.city ? `, ${selectedParty.city}` : ""}
            </Text>
          </Text>
          <Text size="sm" c="dimmed">
            {t("sourcePanel.contact")}:{" "}
            <Text component="span" c="dark.6">
              {selectedParty.contactName ?? "—"}
            </Text>
            {selectedParty.contactPhone ? `, ${selectedParty.contactPhone}` : ""}
          </Text>
        </Box>
      )}
      <Anchor
        component="button"
        type="button"
        size="sm"
        onClick={() => setOverrideOpen((v) => !v)}
      >
        <Group gap={4} wrap="nowrap">
          {overrideOpen ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
          <Text component="span" size="sm">
            {t("sourcePanel.override")}
          </Text>
        </Group>
      </Anchor>
      <Collapse expanded={overrideOpen}>
        <Stack gap="sm">
          <Group grow>
            <TextInput
              label={t("sourcePanel.contactName")}
              placeholder={t("sourcePanel.staffNamePlaceholder")}
              value={externalPartyContactName}
              onChange={(e) => onContactNameChange(e.currentTarget.value)}
            />
            <TextInput
              label={t("sourcePanel.phone")}
              placeholder="+976..."
              value={externalPartyContactPhone}
              onChange={(e) => onContactPhoneChange(e.currentTarget.value)}
            />
          </Group>
          <TextInput
            label={t("sourcePanel.addressOverride")}
            placeholder={t("sourcePanel.addressOverridePlaceholder")}
            value={externalPartyAddressOverride}
            onChange={(e) => onAddressOverrideChange(e.currentTarget.value)}
          />
        </Stack>
      </Collapse>
    </>
  )
}

export function InboundSourcePanel(props: Props) {
  const {
    sourceType,
    fromWarehouseId,
    toWarehouseId,
    externalPartyId,
    externalPartyContactName,
    externalPartyContactPhone,
    externalPartyAddressOverride,
    errors,
    onFromWarehouseChange,
    onToWarehouseChange,
    onExternalPartyChange,
    onContactNameChange,
    onContactPhoneChange,
    onAddressOverrideChange,
  } = props

  const { t } = useTranslation("inbound")
  const warehouses = useWarehouseOptions()

  const warehousePlaceholder = warehouses.isLoading
    ? t("sourcePanel.loading")
    : warehouses.options.length === 0
      ? t("sourcePanel.noWarehouse")
      : t("sourcePanel.selectWarehouse")

  if (sourceType === "INTERNAL_WAREHOUSE") {
    return (
      <Stack gap="sm">
        <Group grow align="flex-start">
          <Select
            label={t("sourcePanel.sourceWarehouse")}
            placeholder={warehousePlaceholder}
            data={warehouses.options}
            value={fromWarehouseId || null}
            onChange={(v) => onFromWarehouseChange(v ?? "")}
            error={errors.fromWarehouseId ?? undefined}
            searchable
            required
          />
          <Select
            label={t("sourcePanel.destinationWarehouse")}
            placeholder={warehousePlaceholder}
            data={warehouses.options}
            value={toWarehouseId || null}
            onChange={(v) => onToWarehouseChange(v ?? "")}
            error={errors.toWarehouseId ?? undefined}
            searchable
            required
          />
        </Group>
        {!warehouses.isLoading && warehouses.options.length === 0 && (
          <Text size="sm" c="dimmed">
            {t("sourcePanel.noWarehouseHint")}
            <Anchor size="sm" component={Link} to="/settings">
              Settings → Warehouses
            </Anchor>
            {t("sourcePanel.noWarehouseHintSuffix")}
          </Text>
        )}
      </Stack>
    )
  }

  return (
    <Stack gap="sm">
      <Select
        label={t("sourcePanel.destinationWarehouse")}
        placeholder={warehousePlaceholder}
        data={warehouses.options}
        value={toWarehouseId || null}
        onChange={(v) => onToWarehouseChange(v ?? "")}
        error={errors.toWarehouseId ?? undefined}
        searchable
        required
      />
      <ExternalPartyFields
        externalPartyId={externalPartyId}
        externalPartyContactName={externalPartyContactName}
        externalPartyContactPhone={externalPartyContactPhone}
        externalPartyAddressOverride={externalPartyAddressOverride}
        externalPartyIdError={errors.externalPartyId}
        onExternalPartyChange={onExternalPartyChange}
        onContactNameChange={onContactNameChange}
        onContactPhoneChange={onContactPhoneChange}
        onAddressOverrideChange={onAddressOverrideChange}
      />
    </Stack>
  )
}
